/**
 * Iframe Telemetry - Track and monitor widget loads from external domains
 * Detects suspicious/unauthorized hosts loading the widget
 */
import * as Sentry from "@sentry/react";
import { isSuspiciousDomain } from "../constants/security-patterns";

/**
 * Get authorized domains from environment or use defaults
 * These should match FRAME_ANCESTORS from .dev.vars
 */
function getAuthorizedHosts(): string[] {
  // Always include Blockdaemon domains
  const blockdaemonDomains = [
    "blockdaemon.com",
    "www.blockdaemon.com",
    "app.blockdaemon.com",
    "staging.blockdaemon.com",
    "stg.blockdaemon.com",
    "dev.blockdaemon.com",
  ];

  // Try to get additional domains from environment (build-time injection)
  const frameAncestors = import.meta.env?.VITE_FRAME_ANCESTORS || "";

  if (frameAncestors && frameAncestors !== "'self'") {
    // Parse FRAME_ANCESTORS format: "http://localhost:8080 https://example.com"
    const additionalHosts = frameAncestors
      .split(/\s+/)
      .map((url: string) => {
        try {
          if (url.startsWith("http://") || url.startsWith("https://")) {
            return new URL(url).hostname;
          }
          return url;
        } catch {
          return url;
        }
      })
      .filter((host: string) => host && host !== "'self'" && host !== "'none'");

    // Combine Blockdaemon domains with customer domains
    return [...new Set([...blockdaemonDomains, ...additionalHosts])];
  }

  // Return Blockdaemon domains by default
  return blockdaemonDomains;
}

// Get authorized hosts once at module load
const AUTHORIZED_HOSTS = getAuthorizedHosts();

/**
 * Check if we're running in an iframe
 */
function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // Blocked by same-origin policy = definitely in iframe
  }
}

/**
 * Get the parent domain hosting the iframe
 */
function getParentDomain(): string {
  try {
    // In development, if we can access parent directly, we're same-origin
    if (import.meta.env.DEV) {
      try {
        // If we can access parent.location, we're same-origin (likely localhost)
        if (window.parent?.location && window.parent !== window) {
          return new URL(window.parent.location.href).hostname;
        }
      } catch {
        // Cross-origin, continue with normal detection
      }
    }

    // Try to get parent domain
    if (window.location.ancestorOrigins?.length > 0) {
      return new URL(window.location.ancestorOrigins[0]).hostname;
    }
    // Fallback to document.referrer
    if (document.referrer) {
      return new URL(document.referrer).hostname;
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * Check if domain is authorized
 */
function isAuthorizedDomain(domain: string): boolean {
  return AUTHORIZED_HOSTS.some(
    (authorized) => domain === authorized || domain.endsWith(`.${authorized}`),
  );
}

/**
 * Report unauthorized iframe load to Sentry
 */
function reportUnauthorizedLoad(domain: string, severity: "warning" | "error"): void {
  Sentry.withScope((scope) => {
    scope.setTag("security_type", "iframe_telemetry");
    scope.setTag("host_domain", domain);
    scope.setTag("authorized", "false");
    scope.setLevel(severity);

    scope.setContext("iframe_load", {
      parent_domain: domain,
      is_suspicious: isSuspiciousDomain(domain),
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
      referrer: document.referrer,
    });

    const message = isSuspiciousDomain(domain)
      ? `SUSPICIOUS: Widget loaded from potentially malicious domain: ${domain}`
      : `UNAUTHORIZED: Widget loaded from unrecognized domain: ${domain}`;

    Sentry.captureMessage(message, severity);
  });
}

/**
 * Initialize iframe telemetry monitoring
 */
export function initializeIframeTelemetry(): void {
  // Only run if in iframe
  if (!isInIframe()) {
    return;
  }

  const parentDomain = getParentDomain();

  // Check authorization
  if (isAuthorizedDomain(parentDomain)) {
    // Still track authorized loads for analytics
    Sentry.addBreadcrumb({
      category: "iframe.load",
      message: `Widget loaded from authorized domain: ${parentDomain}`,
      level: "info",
      data: { domain: parentDomain },
    });
  } else if (isSuspiciousDomain(parentDomain)) {
    // 🚨 SUSPICIOUS domain detected
    reportUnauthorizedLoad(parentDomain, "error");
  } else {
    // ⚠️ Unauthorized domain: ${parentDomain}
    reportUnauthorizedLoad(parentDomain, "warning");
  }
}
